import React, {
  useContext,
  useMemo,
} from 'react';
import moment from 'moment';
import { PropTypes } from 'prop-types';
import copy from 'clipboard-copy';
import { CheckCircleOutline, RestoreFromTrash } from '@mui/icons-material';

import format from 'string-template';
import useSessionStore from '../../../../../hooks/useSessionStore';
import useUserDoc from '../../../../hooks/useUserDoc';
import feedType, { feedOrderingActivityTypes } from '../../../../context/FeedContext/config/feedTypes';
import FeedContext from '../../../../context/FeedContext';
import useFeed from '../../../../hooks/useFeed';

import { DateFormat } from '../../../../../utils/date';
import CheckInInfo from '../CheckInInfo';
import ClaimButton from './ClaimActivity';
import {
  HeaderContainer,
  ActionButtonsContainer,
  RestoreButton,
  MarkAsReadButton,
  StyledUserProfileCard,
  LeftSection,
} from './styles';
import texts from './texts.json';
import actionBy from './actionBy';

const ActivityHeader = ({
  activity,
  clientUserDoc,
}) => {
  const { onMarkAsRead, onMarkTaskComplete, onRestore } = useContext(FeedContext);
  const { readOnlyMode } = useFeed();
  const { isCoachAssistant, isAdmin } = useSessionStore();

  const {
    time,
    type,
    user: userId,
    userName,
    userAvatar,
    createdAt,
    isFirstCheckIn,
    enabledFeatures,
    weeklyWeightGoal,
  } = activity;

  const isCoachTask = type === feedType.COACH_TASK;
  // Coach-scoped via FeedActivity's viewAsCoachId pin, so a multi-coach client's
  // submission for another coach can't satisfy this feed's check.
  const hasPostPaymentFormAnswer = !!clientUserDoc?.postPaymentFormSubmittedAt;
  const {
    userDoc: actionUserDoc,
  } = useUserDoc(activity.actionBy !== actionBy.SERVER_FUNCTION
    && activity.actionBy !== actionBy.SYSTEM
    ? activity.actionBy
    : null);

  const activityTime = useMemo(() => (
    moment.utc(time?.toDate()).local().fromNow()
  ), [
    time,
  ]);

  const initialTimeLabel = useMemo(() => {
    if (!moment(time?.toDate()).isSame(moment(createdAt?.toDate()))) {
      return `(${texts.initialTimeLabel} ${moment(createdAt?.toDate()).local().fromNow()})`;
    }
    return '';
  }, [
    time,
    createdAt,
  ]);

  const activityTimeText = `${activityTime} ${initialTimeLabel}`;

  const subLine = format(texts.archivedDetails, {
    actionBy: (`${actionUserDoc?.data?.firstName || ''} ${actionUserDoc?.data?.lastName || ''}`.trim())
    || texts.archivedBySystem,
    actionAt: moment(activity?.actionAt?.toDate()).format(DateFormat.DATE_MONTH_YEAR_TIME_SHORT_DISPLAY_FORMAT),
  });

  // Coach tasks reuse the same header (the doc carries the coach as `user`); only
  // the subtitle differs, showing who assigned the task. Only admins see the assigner's name.
  const subTitle = isCoachTask && isAdmin && activity.assignedByName
    ? `${format(texts.coachTaskAssignedBy, { assignedByName: activity.assignedByName })} · ${activityTimeText}`
    : activityTimeText;

  return (
    <HeaderContainer onClick={() => copy(activity.id)}>
      <LeftSection>
        <StyledUserProfileCard
          clientId={userId}
          dataRow={{
            userName,
            userAvatar,
            serviceStartAt: clientUserDoc?.serviceStartAt,
            postPaymentFormSubmittedAt: clientUserDoc?.postPaymentFormSubmittedAt?.toDate(),
          }}
          subTitle={subTitle}
          className="user-name"
          subLine={activity?.type === feedType.ARCHIVED && isCoachAssistant ? subLine : undefined}
          hasPostPaymentFormAnswer={hasPostPaymentFormAnswer}
        />
        {type === feedType.CHECK_IN && (
          <CheckInInfo
            isFirstCheckIn={isFirstCheckIn}
            enabledFeatures={enabledFeatures}
            weeklyWeightGoal={weeklyWeightGoal}
          />
        )}
      </LeftSection>
      <ActionButtonsContainer>
        {isCoachAssistant && feedOrderingActivityTypes.includes(type) && (
          <ClaimButton activity={activity} />
        )}
        {type === feedType.ARCHIVED ? (
          <RestoreButton
            startIcon={<RestoreFromTrash />}
            disabled={readOnlyMode}
            onClick={() => onRestore(activity)}
          >
            {texts.RESTORE}
          </RestoreButton>
        ) : (
          <MarkAsReadButton
            startIcon={<CheckCircleOutline />}
            disabled={readOnlyMode}
            onClick={() => (isCoachTask
              ? onMarkTaskComplete(activity)
              : onMarkAsRead(activity))}
          >
            {isCoachTask ? texts.MARK_AS_COMPLETED : texts.MARK_AS_READ}
          </MarkAsReadButton>
        )}
      </ActionButtonsContainer>
    </HeaderContainer>
  );
};

ActivityHeader.propTypes = {
  activity: PropTypes.object.isRequired,
  clientUserDoc: PropTypes.object,
};

ActivityHeader.defaultProps = {
  clientUserDoc: null,
};

export default ActivityHeader;
